/** Correlation identifiers only. These values must never grant access or a benefit. */
export const WINBACK_TRACE_PARAM = "mpr_trace";
export const WINBACK_ENTRY_PARAM = "mpr_entry";
export const WINBACK_HANDOFF_PARAM = "mpr_handoff";

const OPAQUE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type WinbackLinkContext = {
  traceId: string;
  entryId: string | null;
  handoff: "auto" | "explicit" | null;
};

export function readWinbackOpaqueId(value: unknown): string | null {
  return typeof value === "string" && OPAQUE_ID.test(value) ? value.toLowerCase() : null;
}

function singleValue(search: URLSearchParams, key: string): string | null {
  const values = search.getAll(key);
  return values.length === 1 ? values[0] : null;
}

export function readWinbackLinkContext(source: URLSearchParams | string): WinbackLinkContext | null {
  const search = typeof source === "string" ? new URLSearchParams(source) : source;
  const traceId = readWinbackOpaqueId(singleValue(search, WINBACK_TRACE_PARAM));
  if (!traceId) return null;
  const entryId = readWinbackOpaqueId(singleValue(search, WINBACK_ENTRY_PARAM));
  const marker = singleValue(search, WINBACK_HANDOFF_PARAM);
  // Preserve a valid claim even without an entry ID (e.g. opted-out tracking).
  // The server classifies an unmatched claim as unknown, never verified handoff.
  return { traceId, entryId, handoff: marker === "auto" || marker === "explicit" ? marker : null };
}

/** Never copy arbitrary query params (OAuth/provider secrets can appear here). */
export function copyWinbackLinkContext(source: URLSearchParams | string, target: URLSearchParams) {
  const context = readWinbackLinkContext(source);
  if (!context) return;
  target.set(WINBACK_TRACE_PARAM, context.traceId);
  if (context.entryId) target.set(WINBACK_ENTRY_PARAM, context.entryId);
  if (context.handoff) target.set(WINBACK_HANDOFF_PARAM, context.handoff);
}

export function winbackHandoffAttemptKey(source: URLSearchParams | string): string | null {
  const context = readWinbackLinkContext(source);
  return context ? `winback:${context.traceId}:${context.entryId ?? "initial"}` : null;
}
